import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ToastContainer, toast } from "react-toastify";
import StudentTemplate from "../../component/StudentTemplate";
import Api from '../../component/Api';
import PDFModal from '../../component/PDFModal';
import useAuth from '../../component/useAuth.js';
import { useNavigate } from 'react-router-dom';

const SelectWithAdd = ({
  options = [],
  value = null,
  onChange,
  placeholder = 'Select…',
  isClearable = false,
  isDisabled = false,
  styles = {},
  addLabel = 'Add new…',
  disableAdd = false,
  onInputChange,
  isValidNewOption,
  onCreateOption,
}) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setAdding(false); setInputVal(''); setSearchVal('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  useEffect(() => {
    if (open && value && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  const controlStyles = styles.control ? styles.control({}) : {};
  const valueContainerStyles = styles.valueContainer ? styles.valueContainer({}) : {};
  const indicatorsStyles = styles.indicatorsContainer ? styles.indicatorsContainer({}) : {};
  const menuStyles = styles.menu ? styles.menu({}) : {};

  const handleSelect = (opt) => { onChange(opt); setOpen(false); setSearchVal(''); };

  const handleClear = (e) => { e.stopPropagation(); onChange(null); };

  const isValid = isValidNewOption ? isValidNewOption(inputVal) : inputVal.trim() !== '';

  const handleAdd = () => {
    if (!isValid) return;
    const v = inputVal.trim();
    if (!v) return;

    if (onCreateOption) {
      onCreateOption(v);
    } else if (onChange) {
      onChange({ value: v, label: v, __isNew__: true });
    }

    setAdding(false); setInputVal(''); setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
    if (e.key === 'Escape') { setAdding(false); setInputVal(''); }
  };

  const handleInputChange = (e) => {
    let val = e.target.value;
    if (onInputChange) {
      const result = onInputChange(val, { action: 'input-change' });
      if (result !== undefined) {
        val = result;
      }
    }
    setInputVal(val);
  };

  let currentBorderColor = controlStyles.border ? controlStyles.border.split('solid ')[1] : '#cccccc';
  const isWarningBorder = controlStyles.border && controlStyles.border.includes('#f5a70bff');

  if (open) {
    currentBorderColor = '#2684ff';
  } else if (isHovered) {
    currentBorderColor = isWarningBorder ? '#f5a70bff' : '#b3b3b3';
  }

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchVal.toLowerCase())
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', userSelect: 'none', width: '100%' }}>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => !isDisabled && setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: controlStyles.backgroundColor || '#fff',
          border: `1px solid ${currentBorderColor}`,
          ...(isWarningBorder && !open && !isHovered ? { border: controlStyles.border } : {}),
          borderRadius: controlStyles.borderRadius || '4px',
          minHeight: controlStyles.minHeight || '38px',
          height: controlStyles.height || 'auto',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.6 : 1,
          boxShadow: open ? '0 0 0 1px #2684ff' : (controlStyles.boxShadow || 'none'),
          transition: 'border-color 0.1s, box-shadow 0.1s',
          boxSizing: 'border-box',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          overflow: 'hidden',
          padding: valueContainerStyles.padding || '2px 8px',
          height: valueContainerStyles.height || 'auto',
        }}>
          <span style={{
            color: value ? '#212529' : '#6c757d',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {value ? value.label : placeholder}
          </span>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          height: indicatorsStyles.height || 'auto',
        }}>
          {isClearable && value && !isDisabled && (
            <div onClick={handleClear}
              style={{ padding: '0 4px', color: '#ccc', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#999'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#ccc'}
            >
              <svg height="14" width="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false" style={{ fill: 'currentColor' }}>
                <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
              </svg>
            </div>
          )}
          {isClearable && value && !isDisabled && (
            <span style={{ width: 1, backgroundColor: '#ccc', margin: '4px 0', alignSelf: 'stretch' }} />
          )}
          <div style={{ padding: '0 8px', color: '#ccc', display: 'flex', alignItems: 'center' }}>
            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" style={{ fill: 'currentColor', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
              <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
            </svg>
          </div>
        </div>
      </div>

      {open && !isDisabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #cccccc', borderRadius: '4px',
          boxShadow: '0 4px 11px hsla(0,0%,0%,0.1)', zIndex: menuStyles.zIndex || 9999,
          display: 'flex', flexDirection: 'column',
          ...menuStyles
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid #eee', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <input
              autoFocus
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', height: 30, padding: '0 8px',
                border: '1px solid #ddd', borderRadius: 4,
                fontSize: 13, outline: 'none', color: '#333',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = '#2684ff'}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: '260px', padding: '4px 0' }}>
            {filteredOptions.length === 0 && <div style={{ padding: '8px 12px', color: '#999', fontSize: '14px', textAlign: 'center' }}>No results</div>}
            {filteredOptions.map((opt) => {
              const isSelected = value?.value === opt.value;
              return (
                <div
                  key={opt.value}
                  ref={isSelected ? selectedRef : null}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: '14px',
                    background: isSelected ? '#2684ff' : 'transparent',
                    color: isSelected ? '#fff' : '#333',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#deebff'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
          {!disableAdd && (
            <div style={{ borderTop: '1px solid #cccccc', flexShrink: 0, background: '#fff', borderRadius: '0 0 4px 4px' }}>
              {!adding ? (
                <div
                  onClick={() => setAdding(true)}
                  style={{
                    padding: '10px 12px', cursor: 'pointer', fontSize: '14px',
                    color: '#2684ff', display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#deebff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <span style={{
                    width: 18, height: 18, borderRadius: '50%', background: '#2684ff',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, lineHeight: 1, flexShrink: 0, paddingBottom: 2
                  }}>+</span>
                  {addLabel}
                </div>
              ) : (
                <div style={{ padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    ref={inputRef}
                    value={inputVal}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Type and press Enter…"
                    style={{
                      flex: 1, height: 32, padding: '0 8px',
                      border: '1px solid #2684ff', borderRadius: 4,
                      fontSize: 14, outline: 'none', color: '#333',
                      boxShadow: '0 0 0 1px #2684ff'
                    }}
                  />
                  <button onClick={handleAdd} type="button"
                    disabled={!isValid}
                    style={{ height: 32, padding: '0 12px', background: isValid ? '#2684ff' : '#a0c6ff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, cursor: isValid ? 'pointer' : 'not-allowed' }}>
                    Add
                  </button>
                  <button onClick={() => { setAdding(false); setInputVal(''); setSearchVal(''); }} type="button"
                    style={{ height: 32, padding: '0 10px', background: 'transparent', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, cursor: 'pointer', color: '#666' }}>
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Select = (props) => <SelectWithAdd {...props} disableAdd={true} />;
const CreatableSelect = (props) => <SelectWithAdd {...props} />;

const AcademicCredit = () => {

  const [getEventData, setEventData] = useState({});
  const [getEventStatusData, setEventStatusData] = useState("closed");
  const [loading, setlodaing] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [syllabusDocuments, setSyllabusDocuments] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [nheqfLevels, setNheqfLevels] = useState([]);
  const [courseCategory, setCourseCategory] = useState([]);
  const [applications, setApplications] = useState([]);
  const [approvedCourses, setApprovedCourses] = useState([]);
  const [courseInputValue, setCourseInputValue] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [existingSyllabusDocuments, setExistingSyllabusDocuments] = useState([]);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: null, name: null, type: null });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [courseDisplayLabel, setCourseDisplayLabel] = useState('');
  const [editingApplication, setEditingApplication] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);

  const formTopRef = useRef(null);
  const formSectionRef = useRef(null);

  const navigate = useNavigate();
  const isAuth = useAuth();
  if (isAuth === false) navigate("/");

  const [formData, setFormData] = useState({
    courseName: '',
    course_code: '',
    discipline: '',
    category: '',
    nheqfLevel: '',
    platform: '',
    credits: '',
    gradeScale: '',
    mode: '',
    grade: '',
    gradePoint: '',
    percentage: '',
    abcAvailable: false,
    document: null,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePreview = (doc) => {
    if (isMobile) {
      window.open(doc.url, '_blank');
    } else {
      setPdfModal({ isOpen: true, url: doc.url, name: doc.name, type: doc.file.type });
    }
  };

  const { http } = Api();

  const creditOptions = ['7.0', '6.5', '6.0', '5.5', '5.0', '4.5', '4.0', '3.5', '3.0', '2.5', '2.0', '1.5', '1.0'];
  const grades = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D'];
  const gradeOptions = grades.map((g) => ({ value: g, label: g }));
  const creditSelectOptions = creditOptions.map((c) => ({ value: c, label: c }));

  const gradeScaleOptions = [
    { value: "10", label: "10 Point Scale" },
    { value: "7", label: "7 Point Scale" },
    { value: "5", label: "5 Point Scale" },
    { value: "4", label: "4 Point Scale" },
  ];

  const modeOptions = [
    { value: "online", label: "Online" },
    { value: "offline", label: "Offline" },
    { value: "hybrid", label: "Hybrid" }
  ];

  const platformOptions = [
    { value: "SWAYAM", label: "SWAYAM" },
    { value: "NPTEL", label: "NPTEL" },
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#fff",
      border: "1px solid #dee2e6",
      boxShadow: "none",
      minHeight: "48px",
      borderRadius: "0.375rem",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 12px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    placeholder: (base) => ({ ...base, color: "#6c757d" }),
    singleValue: (base) => ({ ...base, color: "#212529" }),
    indicatorsContainer: (base) => ({ ...base, height: "48px" }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const editSelectStyles = {
    ...selectStyles,
    control: (base) => ({
      ...selectStyles.control(base),
      border: "1.5px solid #f5a70bff",
      backgroundColor: "#ffffffff",
    }),
    singleValue: (base) => ({ ...base, color: "#000000ff" }),
  };

  const smallSelectStyles = {
    control: (base) => ({ ...base, minHeight: "31px", height: "31px" }),
    valueContainer: (base) => ({ ...base, height: "31px", padding: "0 8px" }),
    indicatorsContainer: (base) => ({ ...base, height: "31px" }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const editSmallSelectStyles = {
    ...smallSelectStyles,
    control: (base) => ({
      ...smallSelectStyles.control(base),
      border: "1.5px solid #f5a70bff",
      backgroundColor: "#ffffffff",
    }),
  };

  const studentAdmissionYear = localStorage.getItem("student_admission_year");
  const studentSchemeID = localStorage.getItem("student_scheme_id");

  useEffect(() => {
    setlodaing(true);

    http.get('/Student/getActiveEventsForCreditApplication', {
      params: {
        event_id: 36,
        scheme_id: studentSchemeID,
        admission_year: studentAdmissionYear
      }
    })
      .then(response => {
        const eventRow = response.data?.data?.[0] || {};
        setEventData(eventRow);
        setEventStatusData(eventRow.status || "closed");
        setlodaing(false);
      }).catch(error => {
        setlodaing(false);
        setEventData({});
        setEventStatusData("closed");

        const status = error.response?.status;
        const data = error.response?.data;
        if (error.response?.data?.http_status === 401 && error.response?.data?.status_code == "ERR9") {
          toast.error("Invalid Token");
          setTimeout(() => { navigate("/"); }, 5000);
        }
        else if (status === 401) {
          toast.error(data?.message || "Invalid Token");
          setTimeout(() => { navigate("/"); }, 3000);
        }
        if (error.response?.data?.http_status === 500 && error.response?.data?.status_code == "ERR5") {
          toast.error("Event is closed.Application time is over");
        }
        else {
          toast.error("Something went wrong");
        }
      });
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await http.get('/Student/getActiveApplications');
      if (res.data && res.data.status) setApplications(res.data.data || []);
    } catch (err) {
    }
  };

  const openSecurePdf = async (encryptedFile, fileName, documentId) => {
    setPdfLoadingId(documentId);
    try {
      const res = await http.get(
        `/College/fetchSecureExternalCreditPdf/${encodeURIComponent(encryptedFile)}`,
        { responseType: 'blob' }
      );
      const contentType = res.headers['content-type'];
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: contentType }));
      if (isMobile) {
        window.open(blobUrl, '_blank');
      } else {
        setPdfModal({ isOpen: true, url: blobUrl, name: fileName, type: contentType });
      }
    } catch (err) {
      toast.error('Failed to load document. Please try again.');
    } finally {
      setPdfLoadingId(null);
    }
  };

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const res = await http.get('/Student/getAllDisciplines');
        if (res.data && res.data.status) {
          setDisciplines(res.data.data || []);
          setNheqfLevels(res.data.levels || []);
          setCourseCategory(res.data.category || []);
          setApprovedCourses(res.data.approvedCourses || []);
        }
      } catch (err) {
      }
    };
    fetchDisciplines();
    fetchApplications();
  }, []);

  const disciplineOptions = useMemo(
    () => disciplines.map((d) => ({ value: d.id, label: d.discipline_name })),
    [disciplines]
  );

  const courseOptions = useMemo(() => {
    if (!approvedCourses || approvedCourses.length === 0) return [];
    const uniqueMap = new Map();

    approvedCourses.forEach(app => {
      if (!app.course_name) return;

      const key = [
        app.course_name.toLowerCase().trim(),
        app.discipline_id,
        app.course_category,
        app.credit,
        app.course_level,
        app.platform,
        app.mode,
        app.grade_scale,
        app.course_code || 'no_code',
      ].join('|');

      if (!uniqueMap.has(key)) {
        const label = app.course_tag
          ? `${app.course_name} - ${app.course_tag}`
          : app.course_name;

        uniqueMap.set(key, { value: key, label, data: app });
      }
    });

    return Array.from(uniqueMap.values());
  }, [approvedCourses]);

  const categoryOptions = useMemo(
    () => courseCategory.map((cat) => ({ value: cat.id, label: cat.category_code })),
    [courseCategory]
  );

  const levelOptions = useMemo(
    () => nheqfLevels.map((lvl) => ({ value: lvl.nheqf_level, label: lvl.nheqf_level })),
    [nheqfLevels]
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCourseNameChange = (newValue) => {
    if (!newValue) {
      setFormData(prev => ({ ...prev, courseName: '', course_id: '', course_code: '' }));
      setCourseDisplayLabel('');
      return;
    }
    const isNew = newValue.__isNew__;
    const appData = newValue.data || {};
    if (isNew) {
      setCourseDisplayLabel(newValue.label);
      setFormData(prev => ({
        ...prev, course_id: null, courseName: newValue.label, course_code: '',
        discipline: '', category: '', nheqfLevel: '', platform: '',
        credits: '', mode: '', gradeScale: '', grade: '', percentage: '', gradePoint: '', abcAvailable: false,
      }));
    } else {
      setCourseDisplayLabel(newValue.label);
      setFormData(prev => ({
        ...prev,
        course_id: newValue.value,
        courseName: appData.course_name,
        course_code: appData.course_code || '',
        discipline: appData.discipline_id || '',
        category: appData.course_category || '',
        nheqfLevel: appData.course_level !== null && appData.course_level !== undefined ? String(parseFloat(appData.course_level)) : '',
        platform: appData.platform || '',
        credits: appData.credit || '',
        mode: appData.mode || '',
        gradeScale: appData.grade_scale || '',
        grade: '', percentage: '', gradePoint: '', abcAvailable: false,
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      courseName: '', course_code: '', discipline: '', category: '', nheqfLevel: '',
      platform: '', credits: '', gradeScale: '', mode: '', grade: '',
      gradePoint: '', percentage: '', abcAvailable: false, document: null,
    });
    setDocuments([]);
    setSyllabusDocuments([]);
    setExistingDocuments([]);
    setExistingSyllabusDocuments([]);
    setCourseDisplayLabel('');
    setCourseInputValue('');
    setEditingApplication(null);
    setIsFormVisible(false);
  };

  const scrollToForm = () => {
    setTimeout(() => {
      if (formSectionRef.current) {
        formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  };

  const handleNewApplication = () => {
    setIsFormVisible(true);
    scrollToForm();
  };

  const handleEdit = (app) => {
    setIsFormVisible(true);

    setTimeout(() => {
      if (formTopRef.current) {
        formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);

    setEditingApplication(app.application_id);
    setCourseInputValue('');
    setDocuments([]);
    setSyllabusDocuments([]);

    const allDocs = app.documents || [];
    setExistingDocuments(allDocs.filter(d => d.document_type === 'certificate'));
    setExistingSyllabusDocuments(allDocs.filter(d => d.document_type === 'syllabus'));

    setFormData({
      courseName: app.course_name || '',
      course_code: app.course_code || '',
      discipline: app.discipline_id ? String(app.discipline_id) : (app.discipline_name || ''),
      category: app.course_category ? String(app.course_category) : '',
      nheqfLevel: app.course_level !== null && app.course_level !== undefined ? String(parseFloat(app.course_level)) : '',
      platform: app.platform || '',
      credits: app.acquired_credits !== null && app.acquired_credits !== undefined ? String(parseFloat(app.acquired_credits)) : '',
      gradeScale: app.grade_scale ? String(app.grade_scale) : '',
      mode: app.mode ? app.mode.toLowerCase() : '',
      grade: app.grade || '',
      gradePoint: app.grade_point !== null && app.grade_point !== undefined ? String(app.grade_point) : '',
      percentage: app.percentage !== null && app.percentage !== undefined ? String(app.percentage) : '',
      abcAvailable: app.ABC_status == 1,
      document: null,
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSizeKB = 2048; // 2 MB
    const maxSizeBytes = maxSizeKB * 1024;
    for (const file of files) {
      if (file.size > maxSizeBytes) {
        toast.error(`File "${file.name}" exceeds the maximum size of 2 MB.`);
        e.target.value = null;
        return;
      }
    }

    setDocuments((prev) => [
      ...prev,
      ...files.map((file) => ({
        file, name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        url: URL.createObjectURL(file),
      }))
    ]);
    e.target.value = null;
  };

  const removeFile = (index) => setDocuments((prev) => prev.filter((_, i) => i !== index));

  const handleSyllabusFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSizeKB = 2048; // 2 MB
    const maxSizeBytes = maxSizeKB * 1024;
    for (const file of files) {
      if (file.size > maxSizeBytes) {
        toast.error(`File "${file.name}" exceeds the maximum size of 2 MB.`);
        e.target.value = null;
        return;
      }
    }

    setSyllabusDocuments((prev) => [
      ...prev,
      ...files.map((file) => ({
        file, name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        url: URL.createObjectURL(file),
      }))
    ]);
    e.target.value = null;
  };

  const removeSyllabusFile = (index) => setSyllabusDocuments((prev) => prev.filter((_, i) => i !== index));

  const validateForm = () => {
    if (!formData.courseName.trim()) { toast.error("Course Name is required."); return false; }
    if (!formData.category) { toast.error("Course Category is required."); return false; }
    if (!formData.nheqfLevel) { toast.error("NHEQF Level is required."); return false; }
    if (!formData.discipline) { toast.error("Discipline is required."); return false; }
    if (!formData.platform.trim()) { toast.error("Platform is required."); return false; }
    if (!formData.mode) { toast.error("Mode is required."); return false; }
    if (!formData.credits) { toast.error("Credit Weightage is required."); return false; }
    if (!formData.gradeScale) { toast.error("Grade Scale is required."); return false; }
    if (!formData.abcAvailable) { toast.error("Credits must be available in ABC to submit."); return false; }
    if (!formData.grade) { toast.error("Grade is required."); return false; }
    if (String(formData.gradePoint).trim() === '') { toast.error("Grade Point is required."); return false; }
    if (!formData.percentage) { toast.error("Percentage is required."); return false; }
    if (Number(formData.percentage) < 0 || Number(formData.percentage) > 100) {
      toast.error("Percentage must be between 0 and 100."); return false;
    }
    if (!editingApplication && documents.length === 0) {
      toast.error("Please upload at least one certificate document."); return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const payload = new FormData();
      payload.append('course_name', formData.courseName);
      payload.append('course_code', formData.course_code);
      payload.append('discipline_id', formData.discipline);
      payload.append('course_category', formData.category);
      payload.append('course_level', formData.nheqfLevel);
      payload.append('platform', formData.platform);
      payload.append('mode', formData.mode);
      payload.append('acquired_credits', formData.credits);
      payload.append('grade_scale', formData.gradeScale);
      payload.append('grade', formData.grade);
      payload.append('grade_point', formData.gradePoint);
      payload.append('percentage', formData.percentage);
      payload.append('ABC_status', formData.abcAvailable ? 1 : 0);
      documents.forEach((doc) => payload.append('documents[]', doc.file));
      syllabusDocuments.forEach((doc) => payload.append('syllabus_documents[]', doc.file));

      let res;
      if (editingApplication) {
        res = await http.post(`/Student/updateExternalCredit/${editingApplication}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await http.post('/Student/submitExternalCredit', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (res.data.status) {
        toast.success(res.data.message || (editingApplication ? 'Application updated successfully!' : 'Submitted successfully!'));
        fetchApplications();
        resetForm();
      } else {
        toast.error(res.data.message || 'Submission failed.');
      }
    } catch (error) {
      toast.error(error.response?.data?.errors ? "Please check all required fields." : 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (app) => {
    const status = app.application_status || '';
    const map = {
      'forwarded_to_sfa': 'bg-warning text-dark border-warning',
      'reverted_to_student': 'bg-warning text-dark border-warning',
      'forwarded_to_principal': 'bg-info text-dark border-info',
      'forwarded_to_university': 'bg-primary text-white border-primary',
      'bos_course_approved': 'bg-primary text-white border-primary',
      'application_approved': 'bg-success text-white border-success',
      'bos_course_rejected': 'bg-danger text-white border-danger',
      'rejected_by_sfa': 'bg-danger text-white border-danger',
      'rejected_by_principal': 'bg-danger text-white border-danger',
      'rejected_by_aca16': 'bg-danger text-white border-danger',
    };
    return map[status] || 'bg-light text-dark border-secondary';
  };

  useEffect(() => { return () => documents.forEach(doc => URL.revokeObjectURL(doc.url)); }, [documents]);
  useEffect(() => { return () => syllabusDocuments.forEach(doc => URL.revokeObjectURL(doc.url)); }, [syllabusDocuments]);

  const ss = editingApplication ? editSelectStyles : selectStyles;
  const sss = editingApplication ? editSmallSelectStyles : smallSelectStyles;

  const TIMELINE_STEPS = [
    { label: 'Submitted' },
    { label: 'SFA Review' },
    { label: 'Principal' },
    { label: 'University' },
    { label: 'Approved' },
  ];

  const STATUS_STEP_MAP = {
    'forwarded_to_sfa': 1, 'rejected_by_sfa': 1, 'reverted_to_student': 1,
    'forwarded_to_principal': 2, 'rejected_by_principal': 2,
    'forwarded_to_university': 3, 'rejected_by_aca16': 3,
    'bos_course_rejected': 3, 'bos_course_approved': 3,
    'application_approved': 4,
  };

  const REJECTED_STATUSES = new Set(['rejected_by_sfa', 'rejected_by_principal', 'rejected_by_aca16', 'bos_course_rejected']);
  const REVERTED_STATUSES = new Set(['reverted_to_student']);

  const getStepProps = (app, i) => {
    const status = app.application_status || '';
    let activeStep;

    if (status === 'reverted_to_student') {
      const remarkName = app?.remarks?.name?.toLowerCase?.() || '';

      if (remarkName.includes('sfa')) activeStep = 1;
      else if (remarkName.includes('principal')) activeStep = 2;
      else if (
        remarkName.includes('aca16')
      ) {
        activeStep = 3;
      } else {
        activeStep = 1;
      }
    } else {
      activeStep = STATUS_STEP_MAP[status] ?? 1;
    }
    const done = i < activeStep || activeStep === 4;
    const active = i === activeStep && activeStep !== 4;
    const isRej = REJECTED_STATUSES.has(status) && active;
    const isRev = REVERTED_STATUSES.has(status) && active;
    let bg = '#f9fafb', border = '#d1d5db', shadow = 'none', content = null;
    if (isRej) { bg = '#ef4444'; border = '#ef4444'; shadow = '0 0 0 3px rgba(239,68,68,.1)'; content = '✕'; }
    else if (isRev) { bg = '#f59e0b'; border = '#f59e0b'; shadow = '0 0 0 3px rgba(245,158,11,.1)'; content = '↩'; }
    else if (done) { bg = '#10b981'; border = '#10b981'; content = '✓'; }
    else if (active) {
      bg = '#fff'; border = '#3b82f6'; shadow = '0 0 0 3px rgba(59,130,246,.12)';
      content = <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />;
    }
    const textColor = isRej ? '#ef4444' : isRev ? '#f59e0b' : active ? '#111827' : done ? '#6b7280' : '#9ca3af';
    const textWeight = active ? 600 : done ? 500 : 400;
    return { done, active, isRej, isRev, bg, border, shadow, content, textColor, textWeight };
  };

  const renderHorizontalTimeline = (app) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {TIMELINE_STEPS.map((step, i) => {
        const p = getStepProps(app, i);
        const leftLine = i === 0 ? 'transparent' : (p.done || p.active) ? '#10b981' : '#e5e7eb';
        const rightLine = i === TIMELINE_STEPS.length - 1 ? 'transparent' : p.done ? '#10b981' : '#e5e7eb';
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div style={{ flex: 1, height: 2, background: leftLine }} />
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${p.border}`, background: p.bg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700, boxShadow: p.shadow, transition: 'all .2s' }}>
                {p.content}
              </div>
              <div style={{ flex: 1, height: 2, background: rightLine }} />
            </div>
            <div style={{ fontSize: 10, marginTop: 5, textAlign: 'center', lineHeight: 1.3, color: p.textColor, fontWeight: p.textWeight, maxWidth: 64 }}>
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderVerticalTimeline = (app) => {
    const status = app.application_status || '';
    const vBadge = (p) => {
      if (!p.active) return null;
      const cfg = p.isRej ? { bg: '#fee2e2', color: '#991b1b', text: 'Rejected' }
        : p.isRev ? { bg: '#fef3c7', color: '#92400e', text: 'Reverted' }
          : status === 'application_approved' ? { bg: '#d1fae5', color: '#065f46', text: 'Approved' }
            : { bg: '#eff6ff', color: '#1d4ed8', text: 'In review' };
      return <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: cfg.bg, color: cfg.color, flexShrink: 0, whiteSpace: 'nowrap', marginTop: 1 }}>{cfg.text}</span>;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>
        {TIMELINE_STEPS.map((step, i) => {
          const p = getStepProps(app, i);
          const isLast = i === TIMELINE_STEPS.length - 1;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
              {/* dot + line */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${p.border}`, background: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700, boxShadow: p.shadow, flexShrink: 0, zIndex: 1 }}>
                  {p.content}
                </div>
                {!isLast && <div style={{ width: 2, flex: 1, minHeight: 10, background: p.done ? '#10b981' : '#e5e7eb', margin: '2px 0' }} />}
              </div>
              {/* label + badge */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, paddingTop: 1, paddingBottom: isLast ? 2 : 14 }}>
                <div>
                  <div style={{ fontSize: 12.5, color: p.textColor, fontWeight: p.textWeight, lineHeight: 1.3 }}>{step.label}</div>
                  {/* {p.active && (p.isRej || p.isRev) && app.remarks?.remark && (
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2, maxWidth: 180, lineHeight: 1.3 }}>
                      {app.remarks.remark.length > 60 ? app.remarks.remark.substring(0, 60) + '…' : app.remarks.remark}
                    </div>
                  )} */}
                </div>
                {vBadge(p)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTimeline = (app) => (
    <div className="w-100">
      <div className="d-none d-md-block">{renderHorizontalTimeline(app)}</div>
      <div className="d-md-none">{renderVerticalTimeline(app)}</div>
    </div>
  );

  return (
    <StudentTemplate>
      <ToastContainer position="top-right" autoClose={3000} />

      {loading ? (
        <div className="container-fluid">
          <div className="row justify-content-center align-items-center">
            <div className="col-sm-12">
              <div className="card h-100">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                  <div className="containeranimate">
                    <div className="dot"></div><div className="dot"></div><div className="dot"></div>
                    <div className="dot"></div><div className="dot"></div><div className="dot"></div>
                    <div className="dot"></div><div className="dot"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : getEventData.status === "active" ? (

        <div ref={formSectionRef} className="container-fluid mb-4" style={{ scrollMarginTop: '80px' }}>
          {!isFormVisible ? (
            <div className="card tranaction-card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom pt-4 pb-3">
                <div className="col-12 col-md-12">
                  <h4 className="mobile text-center">External Course Credit</h4>
                  <p className="text-muted small mb-0 text-center mt-0">Register credits earned from External Platforms</p>
                </div>
              </div>
              <div className="card-body bg-white text-center py-4 rounded-5">
                <button
                  type="button"
                  onClick={handleNewApplication}
                  className="btn btn-primary fw-bold shadow-sm d-inline-flex align-items-center gap-2 px-4 py-2 rounded-3"
                >
                  <i className="fas fa-plus-circle"></i> New Application
                </button>
              </div>
            </div>
          ) : (
            <div
              ref={formTopRef}
              className={`card tranaction-card shadow-sm transition-all duration-300 ${editingApplication ? 'border-warning border-2' : 'border-0'}`}
              style={{ scrollMarginTop: '100px' }}
            >
              <div className={`card-header pt-4 pb-3 ${editingApplication ? 'bg-opacity-10 border-bottom-0' : 'bg-white border-bottom'}`}>
                <div className="col-12 col-md-12">
                  <h4 className="mobile text-center">External Course Credit</h4>
                  <p className="text-muted small mb-0 text-center mt-0">Register credits earned from External Platforms</p>
                </div>
              </div>

              <div className={`px-4 pt-4 pb-2 ${editingApplication ? 'bg-opacity-10' : 'bg-white'}`}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    {editingApplication ? (
                      <h5 className="fw-bold mb-1 text-warning d-flex align-items-center gap-2">
                        <i className="fas fa-edit"></i> Edit Reverted Application
                      </h5>
                    ) : (
                      <h5 className="fw-bold mb-0 text-primary">
                        Application Form
                      </h5>
                    )}
                  </div>
                </div>
              </div>

              <div className={`card-body p-4 rounded-bottom-4 ${editingApplication ? 'bg-opacity-10 rounded-bottom' : 'bg-white'}`}>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="small text-dark fw-bold mb-2 d-block">COURSE NAME<span className="errorclr">*</span></label>
                      <CreatableSelect
                        key={editingApplication ? `edit-${editingApplication}` : (formData.courseName === '' ? 'empty' : 'filled')}
                        isClearable
                        options={courseOptions}
                        value={formData.courseName
                          ? { label: courseDisplayLabel || formData.courseName, value: formData.courseName }
                          : null
                        }
                        inputValue={courseInputValue}
                        onInputChange={(val) => setCourseInputValue(val)}
                        onChange={handleCourseNameChange}
                        placeholder="Select existing or type to add new..."
                        formatCreateLabel={(inputValue) => `Add New Course: "${inputValue}"`}
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        COURSE CODE<span className="fw-normal" style={{ color: '#414242' }}>(Optional)</span>
                      </label>
                      <input
                        type="text"
                        name="course_code"
                        className={`form-control border text-dark ${formData.abcAvailable
                          ? (editingApplication ? 'border-warning bg-white' : 'bg-white')
                          : ''
                          }`}
                        placeholder="Course Code"
                        maxLength={20}
                        style={{ height: '50px' }}
                        value={formData.course_code || ""}
                        onChange={(e) => {
                          let value = e.target.value.toUpperCase();
                          if (value === "" || /^[A-Z0-9_/-]+$/.test(value)) {
                            handleChange({
                              target: { name: "course_code", value: value }
                            });
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="small text-dark fw-bold mb-2 d-block">COURSE CATEGORY<span className="errorclr">*</span></label>
                      <Select
                        options={categoryOptions}
                        value={categoryOptions.find(opt => String(opt.value) === String(formData.category) || opt.label === formData.category) || null}
                        onChange={(selected) => setFormData({ ...formData, category: selected?.value || "" })}
                        placeholder="Select"
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="small text-dark fw-bold mb-2 d-block">NHEQF LEVEL<span className="errorclr">*</span>
                      </label>
                      <Select
                        options={levelOptions}
                        value={formData.nheqfLevel ? levelOptions.find((opt) => parseFloat(opt.value) === parseFloat(formData.nheqfLevel)) : null}
                        onChange={(selected) => setFormData({ ...formData, nheqfLevel: selected?.value || "" })}
                        placeholder="Select"
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="small text-dark fw-bold mb-2 d-block">DISCIPLINE<span className="errorclr">*</span></label>
                    <Select
                      options={disciplineOptions}
                      value={disciplineOptions.find((opt) => String(opt.value) === String(formData.discipline) || opt.label === formData.discipline) || null}
                      onChange={(selected) => setFormData((prev) => ({ ...prev, discipline: selected ? selected.value : '' }))}
                      placeholder="Select Discipline"
                      isClearable
                      styles={ss}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-8 mb-3">
                      <label className="small text-dark fw-bold mb-2 d-block">PLATFORM<span className="errorclr">*</span></label>
                      <CreatableSelect
                        key={editingApplication ? `edit-platform-${editingApplication}` : (formData.platform === '' ? 'empty' : 'filled')}
                        isClearable
                        options={platformOptions}
                        value={formData.platform ? { label: formData.platform, value: formData.platform } : null}
                        onChange={(selected) => setFormData({ ...formData, platform: selected ? selected.value : "" })}
                        onCreateOption={(inputValue) => setFormData({ ...formData, platform: inputValue })}
                        placeholder="Select platform or type to add new"
                        formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="small text-dark fw-bold mb-2 d-block">MODE<span className="errorclr">*</span></label>
                      <Select
                        options={modeOptions}
                        value={modeOptions.find((opt) => opt.value === (formData.mode || '').toLowerCase()) ?? null}
                        onChange={(selected) => setFormData({ ...formData, mode: selected?.value || "" })}
                        placeholder="Select"
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">COURSE CREDIT<span className="errorclr">*</span></label>
                      <Select
                        options={creditSelectOptions}
                        value={formData.credits ? creditSelectOptions.find(opt => parseFloat(opt.value) === parseFloat(formData.credits)) : null}
                        onChange={(selected) => setFormData({ ...formData, credits: selected?.value ?? "" })}
                        placeholder="Select Credits"
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">GRADE SCALE<span className="errorclr">*</span></label>
                      <Select
                        options={gradeScaleOptions}
                        value={gradeScaleOptions.find((opt) => String(opt.value) === String(formData.gradeScale)) ?? null}
                        onChange={(selected) => setFormData({ ...formData, gradeScale: selected?.value || "" })}
                        placeholder="Select Scale"
                        styles={ss}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  <div className={`p-3 rounded-3 mb-3 border ${formData.abcAvailable ? (editingApplication ? 'bg-white border-warning' : 'bg-white border-primary shadow-sm') : 'bg-white border-light'}`}>
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" role="switch" id={`abcSwitch-${editingApplication ? 'edit' : 'new'}`}
                        name="abcAvailable" checked={formData.abcAvailable} onChange={handleChange} />
                      <label className="form-check-label fw-semibold small text-dark" htmlFor={`abcSwitch-${editingApplication ? 'edit' : 'new'}`}>
                        Is credits available in ABC?
                      </label>
                    </div>
                    <div className={`mt-3 ${!formData.abcAvailable ? 'opacity-50 pe-none' : ''}`}>
                      <div className="row g-2">
                        <div className="col-12 col-md-4">
                          <label className="small text-dark fw-bold mb-2 d-block">GRADE<span className="errorclr">*</span></label>
                          <SelectWithAdd
                            options={gradeOptions}
                            value={gradeOptions.find(opt => opt.value === formData.grade) || (formData.grade ? { value: formData.grade, label: formData.grade } : null)}
                            onChange={(selected) => setFormData({ ...formData, grade: selected?.value?.toUpperCase() || "" })}
                            onInputChange={(inputValue, { action }) => {
                              if (action !== "input-change") return inputValue;
                              return inputValue.toUpperCase().replace(/[^A-Z+-]/g, '').slice(0, 3);
                            }}
                            isValidNewOption={(inputValue) => /^[A-Z](\+{1,2}|\-{1,2})?$/.test(inputValue)}
                            placeholder="Select or type grade"
                            isDisabled={!formData.abcAvailable}
                            isClearable
                            styles={sss}
                            addLabel="Add custom grade…"
                          />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="small text-dark fw-bold mb-2 d-block">
                            GRADE POINT<span className="errorclr">*</span>
                          </label>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="10"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className={`form-control form-control-sm border text-dark ${formData.abcAvailable
                              ? (editingApplication ? 'border-warning bg-white' : 'bg-white')
                              : 'bg-light'
                              }`}
                            placeholder="Grade Point"
                            name="gradePoint"
                            value={formData.gradePoint}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (/^\d+$/.test(value) && Number(value) <= 10)) {
                                handleChange(e);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={!formData.abcAvailable}
                          />
                        </div>

                        <div className="col-6 col-md-4">
                          <label className="small text-dark fw-bold mb-2 d-block">
                            PERCENTAGE<span className="errorclr">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            className={`form-control form-control-sm border text-dark ${formData.abcAvailable
                              ? (editingApplication ? 'border-warning bg-white' : 'bg-white')
                              : 'bg-light'
                              }`}
                            placeholder="Percentage"
                            name="percentage"
                            value={formData.percentage}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleChange(e);
                                return;
                              }
                              if (!/^\d{0,3}(\.\d{0,2})?$/.test(value)) return;
                              if (parseFloat(value) > 100) return;
                              handleChange(e);
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={!formData.abcAvailable}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        CERTIFICATE<span className="errorclr">*</span>
                        {editingApplication && <span className="text-muted fw-normal ms-1 small">(Re-upload to replace existing)</span>}
                      </label>

                      {documents.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                          {documents.map((doc, index) => (
                            <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-white rounded-3 border">
                              <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <i className="fas fa-file-alt text-primary"></i>
                                <div className="text-truncate">
                                  <p className="mb-0 small fw-semibold text-dark text-truncate" style={{ maxWidth: '140px' }}>{doc.name}</p>
                                  <p className="mb-0 text-muted" style={{ fontSize: '11px' }}>{doc.size}</p>
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <button type="button" onClick={() => handlePreview(doc)} className="btn btn-sm btn-outline-secondary py-0 px-2"><i className="fas fa-eye"></i></button>
                                <button type="button" onClick={() => removeFile(index)} className="btn btn-sm btn-outline-danger py-0 px-2"><i className="fas fa-trash-alt"></i></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : editingApplication && existingDocuments.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                          {existingDocuments.map((doc) => (
                            <div key={doc.document_id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded-3 border border-warning">
                              <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <i className="fas fa-file-alt text-primary"></i>
                                <div className="text-truncate">
                                  <p className="mb-0 small fw-semibold text-dark text-truncate" style={{ maxWidth: '140px' }}>{doc.file_name}</p>
                                  <p className="mb-0 text-muted" style={{ fontSize: '11px' }}>Uploaded</p>
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => openSecurePdf(doc.encryptedFile, doc.file_name, doc.document_id)}
                                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                                  disabled={pdfLoadingId === doc.document_id}
                                >
                                  {pdfLoadingId === doc.document_id
                                    ? <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }} />
                                    : <i className="fas fa-eye"></i>
                                  }
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setExistingDocuments(prev => prev.filter(d => d.document_id !== doc.document_id))}
                                  className="btn btn-sm btn-outline-danger py-0 px-2"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="position-relative p-3 text-center bg-white rounded-3 transition-all"
                          style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: editingApplication ? '#f59e0b' : '#0d6efd', minHeight: '90px' }}>
                          <input type="file" className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
                            style={{ cursor: 'pointer' }} accept=".pdf,.jpg,.jpeg,.png" multiple onChange={handleFileChange} />
                          <i className={`fas fa-file-arrow-up fa-lg mb-1 d-block mt-2 ${editingApplication ? 'text-warning' : 'text-primary'}`}></i>
                          <p className="small mb-0 text-muted">{editingApplication ? 'Tap to upload new certificate' : 'Tap to upload certificate'}</p>
                          <p className="mb-0 text-muted mt-1" style={{ fontSize: '11px' }}>
                            PDF, JPG, PNG • Max 2MB
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="small text-dark fw-bold mb-2 d-block">
                        SYLLABUS <span className="text-muted fw-normal"></span>
                      </label>

                      {syllabusDocuments.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                          {syllabusDocuments.map((doc, index) => (
                            <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-white rounded-3 border">
                              <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <i className="fas fa-file-alt text-secondary"></i>
                                <div className="text-truncate">
                                  <p className="mb-0 small fw-semibold text-dark text-truncate" style={{ maxWidth: '140px' }}>{doc.name}</p>
                                  <p className="mb-0 text-muted" style={{ fontSize: '11px' }}>{doc.size}</p>
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <button type="button" onClick={() => handlePreview(doc)} className="btn btn-sm btn-outline-secondary py-0 px-2"><i className="fas fa-eye"></i></button>
                                <button type="button" onClick={() => removeSyllabusFile(index)} className="btn btn-sm btn-outline-danger py-0 px-2"><i className="fas fa-trash-alt"></i></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : editingApplication && existingSyllabusDocuments.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                          {existingSyllabusDocuments.map((doc) => (
                            <div key={doc.document_id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded-3 border border-warning">
                              <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <i className="fas fa-file-alt text-secondary"></i>
                                <div className="text-truncate">
                                  <p className="mb-0 small fw-semibold text-dark text-truncate" style={{ maxWidth: '140px' }}>{doc.file_name}</p>
                                  <p className="mb-0 text-muted" style={{ fontSize: '11px' }}>Uploaded</p>
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => openSecurePdf(doc.encryptedFile, doc.file_name, doc.document_id)}
                                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                                  disabled={pdfLoadingId === doc.document_id}
                                >
                                  {pdfLoadingId === doc.document_id
                                    ? <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }} />
                                    : <i className="fas fa-eye"></i>
                                  }
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setExistingSyllabusDocuments(prev => prev.filter(d => d.document_id !== doc.document_id))}
                                  className="btn btn-sm btn-outline-danger py-0 px-2"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="position-relative p-3 text-center bg-white rounded-3"
                          style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: editingApplication ? '#f59e0b' : '#ccc', minHeight: '90px' }}>
                          <input type="file" className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
                            style={{ cursor: 'pointer' }} accept=".pdf,.jpg,.jpeg,.png" multiple onChange={handleSyllabusFileChange} />
                          <i className="fas fa-file-arrow-up fa-lg text-secondary mb-1 d-block mt-2"></i>
                          <p className="small mb-0 text-muted">Tap to upload syllabus</p>
                          <p className="mb-0 text-muted mt-1" style={{ fontSize: '11px' }}>
                            PDF, JPG, PNG • Max 2MB
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row g-2 mt-2 justify-content-center">
                    <div className="col-6 col-md-auto">
                      <button
                        type="submit"
                        disabled={!formData.abcAvailable || isLoading}
                        className="btn btn-primary w-100 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 text-white"
                        style={{ padding: '8px 24px' }}
                      >
                        {isLoading
                          ? <div className="spinner-border spinner-border-sm" role="status"></div>
                          : editingApplication
                            ? <><i className="fas fa-save"></i> Update</>
                            : <><i className="fas fa-paper-plane"></i> Submit</>
                        }
                      </button>
                    </div>
                    <div className="col-6 col-md-auto">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="btn btn-outline-secondary w-100 fw-semibold bg-white shadow-sm"
                        style={{ padding: '8px 24px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div ref={formSectionRef} className="container-fluid mb-4" style={{ scrollMarginTop: "80px" }}>
          <div className="card tranaction-card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom pt-4 pb-3">
              <div className="col-12 col-md-12">
                <h4 className="mobile text-center">External Course Credit</h4>
                <p className="text-muted small mb-0 text-center mt-0">
                  Register credits earned from External Platforms
                </p>
              </div>
            </div>
            <div className="card-body bg-white text-center py-4 rounded-5">
              <div className="alert alert-danger mb-0 fw-semibold">
                <i className="fas fa-circle-xmark me-2"></i>
                Event is closed. Application time is over.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Submission History ── */}
      {applications.length > 0 && (
        <div className="container-fluid mb-4">
          <div
            className="card border-0 overflow-hidden"
            style={{
              borderRadius: 14,
              boxShadow: '0 4px 6px rgba(0,0,0,.04)',
              border: '1px solid #cbd5e1',
              background: '#fff',
            }}
          >
            <div className="card-header bg-primary border-bottom d-flex justify-content-between align-items-center" style={{ padding: '14px 18px' }}>
              <h5 className="mb-0 fw-semibold text-white" style={{ fontSize: 15 }}>Application History</h5>
              <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '2px 9px', borderRadius: 20 }}>
                {applications.length} Records
              </span>
            </div>

            <div className="p-3" style={{ background: '#f1f5f9', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {applications.map((app) => {
                const isOpen = openAccordion === app.application_id;
                const isReverted = app.application_status === 'reverted_to_student';

                return (
                  <div
                    key={app.application_id}
                    className="overflow-hidden"
                    style={{
                      background: '#fff',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      borderLeft: isReverted ? '4px solid #f59e0b' : '1px solid #cbd5e1',
                      boxShadow: isOpen ? '0 4px 12px rgba(0,0,0,0.05)' : '0 1px 3px rgba(0,0,0,0.02)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* ── Header ── */}
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : app.application_id)}
                      style={{
                        padding: '14px 16px',
                        cursor: 'pointer',
                        background: isOpen ? '#f8fafc' : '#fff',
                        borderBottom: isOpen ? '1px solid #e2e8f0' : 'none',
                        userSelect: 'none',
                      }}
                    >
                      <div className="d-flex flex-column flex-md-row justify-content-between gap-2">

                        {/* LEFT SIDE */}
                        <div className="flex-grow-1 min-w-0">

                          {/* Mobile Layout */}
                          <div className="d-block d-md-none">
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: '#0f172a',
                                lineHeight: 1.45,
                                wordBreak: 'break-word',
                                marginBottom: 6,
                              }}
                            >
                              {app.course_name}
                            </div>
                            {app.course_code && (
                              <div style={{ marginBottom: 8 }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    fontSize: 10,
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    fontWeight: 600,
                                  }}
                                >
                                  {app.course_code}
                                </span>
                              </div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                              {[app.platform, `${app.acquired_credits} credits`, `NHEQF ${app.course_level}`, app.mode.toUpperCase()].map((c, ci) => (
                                <span
                                  key={ci}
                                  style={{
                                    fontSize: 11,
                                    padding: '2px 8px',
                                    borderRadius: 20,
                                    background: '#e2e8f0',
                                    color: '#475569',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500
                                  }}
                                >
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Desktop Layout */}
                          <div className="d-none d-md-flex align-items-center gap-3 mb-2">
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#0f172a',
                                lineHeight: 1.4,
                              }}
                            >
                              {app.course_name}
                            </div>
                            {app.course_code && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  fontSize: 11,
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  background: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #bfdbfe',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {app.course_code}
                              </span>
                            )}
                          </div>

                          {/* Meta Chips Desktop */}
                          <div
                            className="d-none d-md-flex flex-wrap align-items-center"
                            style={{
                              gap: '10px',
                              fontSize: 13,
                              color: '#475569',
                              fontWeight: 500,
                              marginTop: 4
                            }}
                          >
                            <span>{app.platform}</span>

                            <span style={{ color: '#CBD5E1' }}>•</span>

                            <span>{app.acquired_credits} Credits</span>

                            <span style={{ color: '#CBD5E1' }}>•</span>

                            <span>NHEQF {app.course_level}</span>

                            <span style={{ color: '#CBD5E1' }}>•</span>

                            <span>{app.mode.toUpperCase()}</span>
                          </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="d-flex align-items-center justify-content-between justify-content-md-end gap-3 mt-2 mt-md-0">
                          <span
                            className={`badge rounded-pill border ${getStatusBadge(app)}`}
                            style={{
                              fontSize: 11,
                              whiteSpace: 'nowrap',
                              padding: '5px 10px'
                            }}
                          >
                            {app.application_label}
                          </span>

                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            style={{
                              flexShrink: 0,
                              transition: 'transform .2s',
                              transform: isOpen ? 'rotate(180deg)' : 'none',
                              color: '#64748b',
                            }}
                          >
                            <polyline
                              points="2.5,4.5 7,9.5 11.5,4.5"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* ── Body ── */}
                    {isOpen && (
                      <div style={{ background: '#f8fafc', paddingBottom: '12px' }}>

                        {[
                          // Timeline
                          <div key="tl" style={{ margin: '12px 12px 0', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', padding: '10px 14px 8px', borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                              Application Status
                            </div>
                            <div style={{ padding: '14px 12px 12px' }}>
                              {renderTimeline(app)}
                            </div>
                          </div>,

                          // Details
                          <div key="det" style={{ margin: '12px 12px 0', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', padding: '10px 14px 8px', borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                              Course Details
                            </div>
                            {[
                              { key: 'Discipline', val: app.discipline_name },
                              { key: 'Category', val: app.category_code },
                              { key: 'Grade Scale', val: app.grade_scale },
                              { key: 'Grade', val: app.grade, sub: `${app.percentage}%  ·  GP ${app.grade_point}` },
                            ].map((row, ri, arr) => (
                              <div key={ri} style={{ display: 'flex', alignItems: 'baseline', padding: '9px 14px', borderBottom: ri < arr.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <span style={{ fontSize: 11.5, color: '#64748b', minWidth: 90, flexShrink: 0, fontWeight: 500 }}>{row.key}</span>
                                <span style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 600 }}>{row.val}</span>
                                {row.sub && <span style={{ fontSize: 11.5, color: '#64748b', marginLeft: 8 }}>{row.sub}</span>}
                              </div>
                            ))}
                          </div>,

                          // Remark
                          app.remarks && (
                            <div key="rem" style={{ margin: '12px 12px 0', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', padding: '10px 14px 8px', borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                Remarks
                              </div>
                              <div style={{ padding: '12px 14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                      <circle cx="8" cy="6" r="3" stroke="#64748b" strokeWidth="1.6" />
                                      <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#64748b" strokeWidth="1.6" strokeLinecap="round" />
                                    </svg>
                                    {app.remarks.name}
                                  </span>
                                  <span style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 500 }}>
                                    {new Date(app.remarks.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </span>
                                </div>
                                <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>{app.remarks.remark}</div>
                              </div>
                            </div>
                          ),

                          // Edit bar
                          isReverted && getEventData.status === 'active' && (
                            <div key="edit" style={{ margin: '12px 12px 0', background: '#fff', borderRadius: 8, border: '1px solid #fcd34d', overflow: 'hidden', boxShadow: '0 1px 2px rgba(245, 158, 11, 0.05)' }}>
                              <div style={{ padding: '12px 14px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="7" stroke="#f59e0b" strokeWidth="1.6" />
                                    <line x1="8" y1="5" x2="8" y2="9" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
                                    <circle cx="8" cy="11.5" r="1" fill="#f59e0b" />
                                  </svg>
                                  Application reverted — please review and resubmit.
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleEdit(app)}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 6, background: editingApplication === app.application_id ? '#d97706' : '#f59e0b', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 1px 3px rgba(245,158,11,.35)', whiteSpace: 'nowrap' }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                    <path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
                                  </svg>
                                  {editingApplication === app.application_id ? 'Editing…' : 'Edit & Resubmit'}
                                </button>
                              </div>
                            </div>
                          ),
                        ]}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <PDFModal
        isOpen={pdfModal.isOpen}
        onClose={() => setPdfModal({ isOpen: false, url: null, name: null, type: null })}
        pdfUrl={pdfModal.url}
        fileName={pdfModal.name}
        fileType={pdfModal.type}
      />
    </StudentTemplate>
  );
};

export default AcademicCredit;